import React, { useState, useEffect, useMemo } from 'react';
import { Booking, Trip } from '../../../types';
import { adminBookingService } from '../../../services/adminBookingService';
import {
  X,
  Clock,
  Armchair,
  Repeat,
  Trash2,
  CheckCircle2,
  MessageSquare,
  ExternalLink,
  Bus,
} from 'lucide-react';

interface AdminBookingActionModalProps {
  booking: Booking;
  allTrips: Trip[];
  adminEmail?: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

export const AdminBookingActionModal: React.FC<AdminBookingActionModalProps> = ({
  booking,
  allTrips,
  adminEmail,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<'TRANSFER' | 'SEAT' | 'POSTPONE' | 'CANCEL'>('TRANSFER');
  const [actionLoading, setActionLoading] = useState(false);

  // Tab 1: Transfer Trip
  const [targetTripDate, setTargetTripDate] = useState<string>(booking.tripDate);
  const [targetTripId, setTargetTripId] = useState<string>('');
  const [targetSeatNumber, setTargetSeatNumber] = useState<number | null>(null);
  const [transferReason, setTransferReason] = useState<string>('');
  const [targetTripOccupiedSeats, setTargetTripOccupiedSeats] = useState<number[]>([]);
  const [loadingSeats, setLoadingSeats] = useState(false);

  // Tab 2: Change Seat in same trip
  const [sameTripSeatNumber, setSameTripSeatNumber] = useState<number | null>(null);
  const [sameTripOccupiedSeats, setSameTripOccupiedSeats] = useState<number[]>([]);

  // Tab 3: Postpone
  const [postponeNotes, setPostponeNotes] = useState<string>('');

  // Tab 4: Cancel
  const [cancelReason, setCancelReason] = useState<string>('');

  // Current Trip
  const currentTrip = useMemo(() => {
    return allTrips.find((t) => t.id === booking.tripId);
  }, [allTrips, booking.tripId]);

  // Load same trip occupied seats
  useEffect(() => {
    if (!currentTrip) return;
    adminBookingService.getTripPassengerManifest(currentTrip).then((res) => {
      const occ = res.passengers.filter((p) => p.isBooked).map((p) => p.seatNumber);
      setSameTripOccupiedSeats(occ);
    });
  }, [currentTrip]);

  // Eligible transfer trips
  const eligibleTrips = useMemo(() => {
    return allTrips.filter(
      (t) =>
        t.id !== booking.tripId &&
        t.universityId === booking.universityId &&
        t.status !== 'CANCELLED' &&
        (!targetTripDate || t.date === targetTripDate)
    );
  }, [allTrips, booking.tripId, booking.universityId, targetTripDate]);

  // Load target trip seats
  useEffect(() => {
    if (!targetTripId) {
      setTargetTripOccupiedSeats([]);
      setTargetSeatNumber(null);
      return;
    }

    const t = allTrips.find((item) => item.id === targetTripId);
    if (!t) return;

    setLoadingSeats(true);
    adminBookingService
      .getTripPassengerManifest(t)
      .then((res) => {
        const occ = res.passengers.filter((p) => p.isBooked).map((p) => p.seatNumber);
        setTargetTripOccupiedSeats(occ);
      })
      .finally(() => setLoadingSeats(false));
  }, [targetTripId, allTrips]);

  // 1. Submit Transfer Trip
  const handleExecuteTransfer = async () => {
    if (!targetTripId || !targetSeatNumber) return;
    const targetTrip = allTrips.find((t) => t.id === targetTripId);
    if (!targetTrip) return;

    setActionLoading(true);
    try {
      const res = await adminBookingService.transferPassengerToTrip({
        bookingId: booking.id,
        oldTripId: booking.tripId,
        oldSeatNumber: booking.seatNumber,
        newTrip: targetTrip,
        newSeatNumber: targetSeatNumber,
        adminEmail,
        reason: transferReason,
      });

      onSuccess(res.message);
      onClose();
    } catch (err: any) {
      alert(`حدث خطأ: ${err?.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Submit Change Seat
  const handleExecuteChangeSeat = async () => {
    if (!sameTripSeatNumber) return;
    setActionLoading(true);
    try {
      const res = await adminBookingService.changePassengerSeat({
        bookingId: booking.id,
        tripId: booking.tripId,
        oldSeatNumber: booking.seatNumber,
        newSeatNumber: sameTripSeatNumber,
        adminEmail,
      });

      onSuccess(res.message);
      onClose();
    } catch (err: any) {
      alert(`حدث خطأ: ${err?.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // 3. Submit Postpone
  const handleExecutePostpone = async () => {
    setActionLoading(true);
    try {
      const res = await adminBookingService.postponeBooking({
        bookingId: booking.id,
        tripId: booking.tripId,
        seatNumber: booking.seatNumber,
        adminEmail,
        postponeNotes,
      });

      onSuccess(res.message);
      onClose();
    } catch (err: any) {
      alert(`حدث خطأ: ${err?.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Submit Cancel
  const handleExecuteCancel = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟ سيتم تحرير المقعد تلقائياً وإتاحته للحجز.')) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await adminBookingService.cancelBooking(
        booking.id,
        booking.tripId,
        booking.seatNumber,
        adminEmail,
        cancelReason
      );

      onSuccess(res.message);
      onClose();
    } catch (err: any) {
      alert(`حدث خطأ: ${err?.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const studentName = booking.studentSnapshot?.fullName || 'الطالب';
  const studentPhone = booking.studentSnapshot?.phoneNumber;
  const waUrl = adminBookingService.getWhatsAppUrl(
    studentPhone,
    `مرحباً يا ${studentName}، بخصوص تذكرتك (${booking.ticketId}) لـ ${booking.universityName}...`
  );

  return (
    <div className="admin-modal-overlay" onClick={onClose} dir="rtl">
      <div
        className="admin-modal-card"
        style={{ maxWidth: '680px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <div className="flex items-center gap-2">
            <Bus size={22} className="text-[#E5232A]" />
            <div>
              <h3 className="text-base font-bold text-slate-900 leading-tight">
                تعديل وتأجيل رحلة: {studentName}
              </h3>
              <span className="text-xs text-slate-500 font-mono">
                كود: {booking.bookingCode} | تذكرة: {booking.ticketId} (مقعد #{booking.seatNumber})
              </span>
            </div>
          </div>
          <button type="button" className="admin-modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="admin-modal-body space-y-4">
          {/* Quick Tabs */}
          <div className="admin-segmented-control w-full flex">
            <button
              type="button"
              className={`admin-segment-btn flex-1 justify-center text-xs ${
                activeTab === 'TRANSFER' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('TRANSFER')}
            >
              <Repeat size={14} />
              <span>تغيير الموعد / اليوم</span>
            </button>

            <button
              type="button"
              className={`admin-segment-btn flex-1 justify-center text-xs ${
                activeTab === 'SEAT' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('SEAT')}
            >
              <Armchair size={14} />
              <span>تغيير المقعد فقط</span>
            </button>

            <button
              type="button"
              className={`admin-segment-btn flex-1 justify-center text-xs ${
                activeTab === 'POSTPONE' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('POSTPONE')}
            >
              <Clock size={14} />
              <span>تأجيل الرحلة</span>
            </button>

            <button
              type="button"
              className={`admin-segment-btn flex-1 justify-center text-xs text-red-600 ${
                activeTab === 'CANCEL' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('CANCEL')}
            >
              <Trash2 size={14} />
              <span>إلغاء الحجز</span>
            </button>
          </div>

          {/* TAB 1: Transfer Trip / Change Date & Slot */}
          {activeTab === 'TRANSFER' && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900">
                اختر التاريخ والرحلة البديلة لنقل حجز الطالب إليها. سيتم تحرير المقعد الحالي (#{booking.seatNumber}) وحجز المقعد الجديد تلقائياً.
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="admin-form-group">
                  <label className="text-xs font-bold">تاريخ الرحلة البديلة:</label>
                  <input
                    type="date"
                    className="admin-search-input w-full"
                    style={{ paddingRight: '1rem' }}
                    value={targetTripDate}
                    onChange={(e) => setTargetTripDate(e.target.value)}
                  />
                </div>

                <div className="admin-form-group">
                  <label className="text-xs font-bold">اختر موعد الرحلة:</label>
                  <select
                    className="admin-filter-select w-full"
                    value={targetTripId}
                    onChange={(e) => setTargetTripId(e.target.value)}
                  >
                    <option value="">-- اختر الرحلة --</option>
                    {eligibleTrips.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.departureTime} ({t.direction === 'RETURN' ? 'عودة' : 'ذهاب'}) — شاغر: {t.availableSeats}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Seat Selector */}
              {targetTripId && (
                <div>
                  <label className="text-xs font-bold block mb-1.5">
                    اختر المقعد في الرحلة البديلة:
                  </label>
                  {loadingSeats ? (
                    <div className="p-4 text-center text-xs text-slate-400">
                      جاري فحص المقاعد...
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))',
                        gap: '6px',
                        maxHeight: '180px',
                        overflowY: 'auto',
                        padding: '10px',
                        background: '#F8FAFC',
                        borderRadius: '12px',
                        border: '1px solid #CBD5E1',
                      }}
                    >
                      {Array.from({ length: 50 }).map((_, idx) => {
                        const sNum = idx + 1;
                        const isOccupied = targetTripOccupiedSeats.includes(sNum);
                        const isSelected = targetSeatNumber === sNum;
                        return (
                          <button
                            key={sNum}
                            type="button"
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '6px 2px',
                              borderRadius: '8px',
                              border: isSelected
                                ? '2px solid #E5232A'
                                : isOccupied
                                ? '1px solid #E2E8F0'
                                : '1px solid #CBD5E1',
                              background: isOccupied
                                ? '#E2E8F0'
                                : isSelected
                                ? '#E5232A'
                                : 'white',
                              color: isOccupied
                                ? '#94A3B8'
                                : isSelected
                                ? 'white'
                                : '#1E293B',
                              cursor: isOccupied ? 'not-allowed' : 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            disabled={isOccupied}
                            onClick={() => setTargetSeatNumber(sNum)}
                          >
                            <Armchair size={13} color={isOccupied ? '#94A3B8' : isSelected ? 'white' : '#64748B'} />
                            <span style={{ fontSize: '11px', fontWeight: 800, fontFamily: 'monospace', marginTop: '2px' }}>
                              #{sNum}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="admin-form-group">
                <label className="text-xs font-bold">سبب التعديل / ملاحظات:</label>
                <input
                  type="text"
                  className="admin-search-input w-full"
                  style={{ paddingRight: '1rem' }}
                  placeholder="مثال: تغيير موعد المحاضرة..."
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-admin-primary w-full flex items-center justify-center gap-2 mt-2"
                disabled={!targetTripId || !targetSeatNumber || actionLoading}
                onClick={handleExecuteTransfer}
              >
                <CheckCircle2 size={16} />
                <span>{actionLoading ? 'جاري نقل الحجز...' : 'تأكيد نقل وتغيير الرحلة'}</span>
              </button>
            </div>
          )}

          {/* TAB 2: Change Seat Only */}
          {activeTab === 'SEAT' && (
            <div className="space-y-4 pt-2">
              <p className="text-xs text-slate-600">
                المقعد الحالي للطالب هو <strong>#{booking.seatNumber}</strong>. اختر المقعد الجديد الشاغر داخل نفس الحافلة:
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))',
                  gap: '6px',
                  maxHeight: '190px',
                  overflowY: 'auto',
                  padding: '10px',
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  border: '1px solid #CBD5E1',
                }}
              >
                {Array.from({ length: currentTrip?.totalSeats || 50 }).map((_, idx) => {
                  const sNum = idx + 1;
                  const isCurrent = sNum === booking.seatNumber;
                  const isOccupied = sameTripOccupiedSeats.includes(sNum) && !isCurrent;
                  const isSelected = sameTripSeatNumber === sNum;
                  return (
                    <button
                      key={sNum}
                      type="button"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px 2px',
                        borderRadius: '8px',
                        border: isCurrent
                          ? '2px solid #F59E0B'
                          : isSelected
                          ? '2px solid #E5232A'
                          : isOccupied
                          ? '1px solid #E2E8F0'
                          : '1px solid #CBD5E1',
                        background: isCurrent
                          ? '#FEF3C7'
                          : isOccupied
                          ? '#E2E8F0'
                          : isSelected
                          ? '#E5232A'
                          : 'white',
                        color: isCurrent
                          ? '#92400E'
                          : isOccupied
                          ? '#94A3B8'
                          : isSelected
                          ? 'white'
                          : '#1E293B',
                        cursor: isOccupied || isCurrent ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      disabled={isOccupied || isCurrent}
                      onClick={() => setSameTripSeatNumber(sNum)}
                    >
                      <Armchair size={13} color={isCurrent ? '#92400E' : isOccupied ? '#94A3B8' : isSelected ? 'white' : '#64748B'} />
                      <span style={{ fontSize: '11px', fontWeight: 800, fontFamily: 'monospace', marginTop: '2px' }}>
                        #{sNum}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="btn-admin-primary w-full flex items-center justify-center gap-2 mt-2"
                disabled={!sameTripSeatNumber || actionLoading}
                onClick={handleExecuteChangeSeat}
              >
                <CheckCircle2 size={16} />
                <span>{actionLoading ? 'جاري التعديل...' : `تأكيد نقل المقعد إلى #${sameTripSeatNumber}`}</span>
              </button>
            </div>
          )}

          {/* TAB 3: Postpone Trip */}
          {activeTab === 'POSTPONE' && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 leading-relaxed">
                سيتم تحرير المقعد رقم #{booking.seatNumber} في رحلة ({booking.tripDate}) فوراً وإتاحته للركاب الآخرين، مع تحويل حالة التذكرة إلى <strong>مؤجلة (POSTPONED)</strong> مع حفظ الرصيد للطالب ليتمكن من تحديد موعد جديد لاحقاً دون خصم مالي.
              </div>

              <div className="admin-form-group">
                <label className="text-xs font-bold">ملاحظات التأجيل:</label>
                <input
                  type="text"
                  className="admin-search-input w-full"
                  style={{ paddingRight: '1rem' }}
                  placeholder="سبب التأجيل أو اتفاق هاتفيا مع الطالب..."
                  value={postponeNotes}
                  onChange={(e) => setPostponeNotes(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-admin-primary bg-amber-600 hover:bg-amber-700 border-amber-600 w-full flex items-center justify-center gap-2"
                disabled={actionLoading}
                onClick={handleExecutePostpone}
              >
                <Clock size={16} />
                <span>تأكيد تأجيل الرحلة وتحرير المقعد</span>
              </button>
            </div>
          )}

          {/* TAB 4: Cancel Booking */}
          {activeTab === 'CANCEL' && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-900 leading-relaxed">
                تحذير: سيتم إلغاء الحجز بشكل نهائي، وتحرير المقعد رقم #{booking.seatNumber} وتحديث حالة التذكرة إلى ملغاة (CANCELLED).
              </div>

              <div className="admin-form-group">
                <label className="text-xs font-bold">سبب الإلغاء:</label>
                <input
                  type="text"
                  className="admin-search-input w-full"
                  style={{ paddingRight: '1rem' }}
                  placeholder="مثال: اعتذار الطالب عن السفر..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-admin-danger w-full flex items-center justify-center gap-2"
                disabled={actionLoading}
                onClick={handleExecuteCancel}
              >
                <Trash2 size={16} />
                <span>تأكيد إلغاء الحجز نهائياً</span>
              </button>
            </div>
          )}
        </div>

        <div className="admin-modal-footer">
          {studentPhone && (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-admin-outline text-emerald-700 border-emerald-300 hover:bg-emerald-50 flex items-center gap-1.5 ml-auto"
            >
              <MessageSquare size={15} />
              <span>تواصل مع الطالب عبر واتساب</span>
              <ExternalLink size={12} />
            </a>
          )}

          <button type="button" className="btn-admin-outline" onClick={onClose}>
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};
